import SectionWrapper from '@/components/feature/SectionWrapper';
import { programs } from '@/mocks/programs';
import SEO, { siteUrl } from '@/components/feature/SEO';

const WHATSAPP_URL = 'https://wa.me/923179699957?text=Hi%20Moin%20Khan%20Boxing%20Academy!%20I%20want%20to%20join%20training';
const program = programs.find((p) => p.id === 'youth-program')!;

const benefits = [
  'Age-appropriate boxing fundamentals',
  'Discipline and respect built into every session',
  'Anti-bullying confidence and self-esteem',
  'Safe, supervised, and structured environment',
  'Physical fitness and coordination development',
  'Character building beyond the ring',
];

const structure = [
  { step: '01', title: 'Introduction', desc: 'Fun, non-contact boxing basics and gym etiquette.' },
  { step: '02', title: 'Fundamentals', desc: 'Stance, movement, and basic punch technique.' },
  { step: '03', title: 'Drills', desc: 'Pad work, bag work, and controlled partner exercises.' },
  { step: '04', title: 'Progress', desc: 'Skill testing, mini-events, and advancement.' },
];

export default function YouthProgram() {
  return (
    <main>
      <SEO
        title="Youth Boxing Program — Moin Khan Boxing Academy | Islamabad"
        description="Age-appropriate boxing training for children and teens in Islamabad. Building discipline, confidence, anti-bullying skills, and physical fitness in a safe, structured environment. Enroll today."
        keywords="youth boxing program, kids boxing training Islamabad, children boxing classes, teen boxing coach, youth martial arts program, kids fitness Islamabad"
        canonicalUrl={siteUrl ? `${siteUrl}/programs/youth-program` : undefined}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            '@id': `${siteUrl}/programs/youth-program#service`,
            name: 'Youth Program',
            url: `${siteUrl}/programs/youth-program`,
            description: 'Age-appropriate boxing training for children and teens focusing on discipline, confidence, anti-bullying skills, and physical fitness in a safe, structured environment.',
            provider: { '@type': 'Organization', name: 'Moin Khan Boxing Academy', '@id': `${siteUrl}/#organization` },
            areaServed: { '@type': 'City', name: 'Islamabad' },
            offers: {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
              areaServed: { '@type': 'City', name: 'Islamabad' },
            },
          },
        ]}
      />
      <section className="relative pt-24 md:pt-28 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={program.image}
            alt={program.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-boxing-black/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-boxing-black via-transparent to-boxing-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <SectionWrapper>
            <span className="section-label">/ Program Detail</span>
            <h1 className="heading-mixed text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              Youth <span className="text-highlight">Program</span>
            </h1>
            <p className="font-poppins text-white/70 text-sm md:text-base max-w-xl mx-auto mt-4">
              Building discipline, confidence, and champions from day one
            </p>
          </SectionWrapper>
        </div>
      </section>

      <section className="bg-boxing-black py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <SectionWrapper>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-white/80 text-sm md:text-base leading-[1.8] mb-8">
                {program.description}
              </p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-base" />
                </span>
                Join Now via WhatsApp
              </a>
            </div>
          </SectionWrapper>
        </div>
      </section>

      <section className="bg-boxing-darker py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <SectionWrapper className="text-center mb-12 md:mb-16">
            <span className="section-label">/ What You Get</span>
            <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl">
              Program <span className="text-highlight">Benefits</span>
            </h2>
          </SectionWrapper>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <SectionWrapper key={b} delay={i * 100}>
                <div className="card-glow bg-boxing-black rounded-xl p-6 flex items-start gap-4">
                  <span className="w-8 h-8 flex items-center justify-center bg-boxing-red rounded-full shrink-0 shadow-red-glow">
                    <i className="ri-check-line text-white text-sm" />
                  </span>
                  <span className="text-white/80 text-sm leading-relaxed">{b}</span>
                </div>
              </SectionWrapper>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-boxing-black py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <SectionWrapper className="text-center mb-12 md:mb-16">
            <span className="section-label">/ The Journey</span>
            <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl">
              Training <span className="text-highlight">Structure</span>
            </h2>
          </SectionWrapper>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {structure.map((s, i) => (
              <SectionWrapper key={s.step} delay={i * 120}>
                <div className="card-glow bg-boxing-darker rounded-xl p-6 md:p-7 text-center h-full">
                  <span className="font-bebas text-4xl md:text-5xl text-boxing-red block mb-3">
                    {s.step}
                  </span>
                  <h4 className="font-oswald uppercase text-sm tracking-wide text-white mb-2">
                    {s.title}
                  </h4>
                  <p className="text-white/60 text-xs md:text-sm leading-relaxed">{s.desc}</p>
                </div>
              </SectionWrapper>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-boxing-darker py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <SectionWrapper>
            <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl">
              Enroll Your <span className="text-highlight">Champion</span>
            </h2>
            <p className="text-white/60 text-sm md:text-base mt-5 mb-8">
              Give your child the gift of discipline and confidence. Message us to learn more.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm md:text-base px-10 py-4"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-lg" />
              </span>
              Join Now via WhatsApp
            </a>
          </SectionWrapper>
        </div>
      </section>
    </main>
  );
}